import React, { useState, useEffect } from "react";
import { Modal, Badge, Row, Col, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getTicketHistory, createTicketHistory, updateTicketHistory, getTicket, updateTicket } from "../api/apiClient";
import formatDate from "../utils/formatDate";

// Mapeo de categorías
const CATEGORY_LABELS = {
  0: "tickets.categoryErrors",
  1: "tickets.categoryInquiries",
  2: "tickets.categorySupportRequest",
  3: "tickets.categoryOther",
};

// Mapeo de estados
const STATUS_MAP = {
  0: { labelKey: "tickets.statusNew", variant: "primary" },
  1: { labelKey: "tickets.statusInProgress", variant: "warning" },
  2: { labelKey: "tickets.statusResolved", variant: "success" },
  3: { labelKey: "tickets.statusClosed", variant: "secondary" },
};

// Mapeo de prioridades
const PRIORITY_MAP = {
  0: { labelKey: "tickets.priorityLow", variant: "info" },
  1: { labelKey: "tickets.priorityMedium", variant: "secondary" },
  2: { labelKey: "tickets.priorityHigh", variant: "warning" },
  3: { labelKey: "tickets.priorityUrgent", variant: "danger" },
};

const MAX_FILES = 5;
const MAX_FILE_SIZE_MB = 10;

const TicketDetailModalAdmin = ({ show, onHide, ticket, onTicketUpdate }) => {
  const { t } = useTranslation();

  // Estados
  const [localTicket, setLocalTicket] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Estados para edición de ticket
  const [isEditingTicket, setIsEditingTicket] = useState(false);
  const [editedStatus, setEditedStatus] = useState(null);
  const [editedPriority, setEditedPriority] = useState(null);

  // Estados para comentarios
  const [newComment, setNewComment] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState("");

  // Cargar ticket completo desde la API
  const loadTicket = async () => {
    if (!ticket?.id) return;

    try {
      const response = await getTicket(ticket.id);
      if (response.success && response.data) {
        setLocalTicket(response.data);
        setEditedStatus(response.data.status);
        setEditedPriority(response.data.priority);
      }
    } catch (error) {
      console.error("Error loading ticket:", error);
      setError(t("tickets.loadError"));
    }
  };

  // Cargar historial de comentarios
  const loadHistory = async () => {
    if (!ticket?.id) return;

    try {
      const response = await getTicketHistory(ticket.id);
      if (response.success && response.data) {
        setHistory(response.data);
      }
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  // Cargar ticket y historial cuando se abre el modal
  useEffect(() => {
    if (show && ticket) {
      setLoading(true);
      setError("");
      Promise.all([loadTicket(), loadHistory()]).finally(() => setLoading(false));
    }
  }, [show, ticket]);

  // Limpiar estados al cerrar
  useEffect(() => {
    if (!show) {
      setNewComment("");
      setCommentFiles([]);
      setFileError("");
      setEditingCommentId(null);
      setEditedCommentText("");
      setIsEditingTicket(false);
      setError("");
    }
  }, [show]);

  // Manejar archivos para comentarios
  const handleFileChange = (e) => {
    setFileError("");
    const selected = Array.from(e.target.files);
    const remaining = MAX_FILES - commentFiles.length;

    if (selected.length > remaining) {
      setFileError(t("tickets.maxFilesExceeded"));
      e.target.value = "";
      return;
    }

    const oversized = selected.find((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (oversized) {
      setFileError(`Archivo muy grande: ${oversized.name}. Máximo ${MAX_FILE_SIZE_MB}MB`);
      e.target.value = "";
      return;
    }

    setCommentFiles((prev) => [
      ...prev,
      ...selected.map((f) => ({
        file: f,
        id: `${f.name}-${Date.now()}-${Math.random()}`,
      })),
    ]);

    e.target.value = "";
  };

  const removeFile = (id) => {
    setCommentFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Guardar cambios en el ticket (status/priority)
  const handleSaveTicket = async () => {
    if (!isEditingTicket) {
      setIsEditingTicket(true);
      return;
    }

    setSubmitting(true);
    try {
      const ticketData = {
        ...localTicket,
        status: editedStatus,
        priority: editedPriority,
      };

      const response = await updateTicket(localTicket.id, ticketData);

      if (response.success) {
        await loadTicket();
        setIsEditingTicket(false);

        if (onTicketUpdate) {
          onTicketUpdate();
        }
      } else {
        setError(response.message || t("tickets.updateError"));
      }
    } catch (error) {
      console.error("Error updating ticket:", error);
      setError(t("tickets.updateError"));
    } finally {
      setSubmitting(false);
    }
  };

  // Agregar comentario
  const handleAddComment = async () => {
    if (!newComment.trim() && commentFiles.length === 0) {
      return;
    }

    setSubmitting(true);
    try {
      const userId = sessionStorage.getItem("idUser");
      const userEmail = sessionStorage.getItem("userEmail");

      const historyData = {
        userId: parseInt(userId, 10),
        userName: userEmail,
        commentText: newComment.trim(),
        isAdmin: true, // Marcar como comentario de admin
      };

      const response = await createTicketHistory(localTicket.id, historyData, commentFiles.map((f) => f.file));

      if (response.success) {
        setNewComment("");
        setCommentFiles([]);
        await Promise.all([loadHistory(), loadTicket()]);
      } else {
        setError(response.message || "Error al agregar comentario");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      setError("Error al agregar comentario");
    } finally {
      setSubmitting(false);
    }
  };

  // Editar comentario
  const handleEditComment = (commentId, currentText) => {
    setEditingCommentId(commentId);
    setEditedCommentText(currentText);
  };

  const handleSaveEditComment = async (commentId) => {
    setSubmitting(true);
    try {
      const response = await updateTicketHistory(commentId, editedCommentText);

      if (response.success) {
        setEditingCommentId(null);
        setEditedCommentText("");
        await loadHistory();
      } else {
        setError(response.message || "Error al actualizar comentario");
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      setError("Error al actualizar comentario");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditedCommentText("");
  };

  if (!localTicket) {
    return null;
  }

  const currentUserId = parseInt(sessionStorage.getItem("idUser"), 10);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{t("tickets.detailTitle")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center my-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Información del Ticket */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("tickets.modalTicketNumber")}</Form.Label>
                  <Form.Control type="text" value={localTicket.ticketNumber || ""} readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("tickets.category")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={t(CATEGORY_LABELS[localTicket.category]) || ""}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("tickets.modalStatus")}</Form.Label>
                  <Form.Select
                    value={editedStatus}
                    onChange={(e) => setEditedStatus(parseInt(e.target.value))}
                    disabled={!isEditingTicket}
                  >
                    <option value={0}>{t("tickets.statusNew")}</option>
                    <option value={1}>{t("tickets.statusInProgress")}</option>
                    <option value={2}>{t("tickets.statusResolved")}</option>
                    <option value={3}>{t("tickets.statusClosed")}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("tickets.modalPriority")}</Form.Label>
                  <Form.Select
                    value={editedPriority}
                    onChange={(e) => setEditedPriority(parseInt(e.target.value))}
                    disabled={!isEditingTicket}
                  >
                    <option value={0}>{t("tickets.priorityLow")}</option>
                    <option value={1}>{t("tickets.priorityMedium")}</option>
                    <option value={2}>{t("tickets.priorityHigh")}</option>
                    <option value={3}>{t("tickets.priorityUrgent")}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>{t("tickets.modalTitle")}</Form.Label>
              <Form.Control type="text" value={localTicket.title || ""} readOnly />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("tickets.description")}</Form.Label>
              <Form.Control as="textarea" rows={3} value={localTicket.description || ""} readOnly />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("tickets.createdAt")}</Form.Label>
                  <Form.Control type="text" value={formatDate(localTicket.createdAt)} readOnly />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("tickets.updatedAt")}</Form.Label>
                  <Form.Control type="text" value={formatDate(localTicket.updatedAt)} readOnly />
                </Form.Group>
              </Col>
            </Row>

            {/* Archivos adjuntos */}
            {localTicket.attachments && localTicket.attachments.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>{t("tickets.attachments")} ({localTicket.attachments.length})</Form.Label>
                <ul>
                  {localTicket.attachments.map((attachment, index) => (
                    <li key={index}>
                      <a href={attachment.fileUrl} target="_blank" rel="noopener noreferrer">
                        {attachment.fileName} ({Math.round(attachment.fileSize / 1024)} KB)
                      </a>
                    </li>
                  ))}
                </ul>
              </Form.Group>
            )}

            <hr />

            {/* Conversación */}
            <h5 className="mb-3">{t("tickets.conversation")}</h5>

            {history.length === 0 ? (
              <p className="text-muted">{t("tickets.noComments")}</p>
            ) : (
              <div className="mb-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
                {history.map((comment) => {
                  const isOwnComment = comment.userId === currentUserId && comment.isAdmin;
                  const isEditing = editingCommentId === comment.id;

                  return (
                    <div key={comment.id} className="mb-3 p-3 border rounded">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <strong>{comment.isAdmin ? "Admin" : comment.userName}</strong>
                          {comment.isAdmin && (
                            <Badge bg="danger" className="ms-2">
                              {t("tickets.adminBadge")}
                            </Badge>
                          )}
                          {comment.isEdited && (
                            <small className="text-muted ms-2">({t("tickets.edited")})</small>
                          )}
                        </div>
                        <small className="text-muted">{formatDate(comment.createdAt)}</small>
                      </div>

                      {isEditing ? (
                        <>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={editedCommentText}
                            onChange={(e) => setEditedCommentText(e.target.value)}
                            className="mb-2"
                          />
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => handleSaveEditComment(comment.id)}
                              disabled={submitting}
                            >
                              {t("tickets.saveButton")}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={handleCancelEdit}>
                              {t("tickets.cancelButton")}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="mb-2">{comment.commentText}</p>
                          {isOwnComment && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEditComment(comment.id, comment.commentText)}
                            >
                              {t("tickets.editButton")}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Agregar comentario */}
            <Form.Group className="mb-3">
              <Form.Label>{t("tickets.addComment")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t("tickets.addCommentPlaceholder")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {t("tickets.attachFiles")} ({t("tickets.optional")})
              </Form.Label>
              <Form.Control type="file" multiple onChange={handleFileChange} />
              <Form.Text className="text-muted">{t("tickets.maxFiles")}</Form.Text>
              {fileError && <Alert variant="danger" className="mt-2">{fileError}</Alert>}
            </Form.Group>

            {commentFiles.length > 0 && (
              <div className="mb-3">
                {commentFiles.map((item) => (
                  <Badge key={item.id} bg="secondary" className="me-2">
                    {item.file.name}
                    <span
                      className="ms-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => removeFile(item.id)}
                    >
                      ×
                    </span>
                  </Badge>
                ))}
              </div>
            )}

            <Button
              variant="primary"
              onClick={handleAddComment}
              disabled={submitting || (!newComment.trim() && commentFiles.length === 0)}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("tickets.sending")}
                </>
              ) : (
                t("tickets.addCommentButton")
              )}
            </Button>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={isEditingTicket ? "success" : "primary"}
          onClick={handleSaveTicket}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {t("common.processing")}
            </>
          ) : isEditingTicket ? (
            t("common.saveChanges")
          ) : (
            t("common.edit")
          )}
        </Button>
        <Button variant="secondary" onClick={onHide}>
          {t("tickets.closeButton")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketDetailModalAdmin;
